import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { CdkMenuItem, MENU_STACK, MenuStack } from '@angular/cdk/menu';
import { VxAvatarComponent } from '../avatar/avatar.component';
import { VxBadgeComponent } from '../badge/badge.component';
import { VxBadgeTone } from '../badge/badge.variants';
import { VxIconComponent } from '../icon/icon.component';
import { VxMenuComponent } from '../menu/menu.component';
import { VX_MENU_ITEM_APPEARANCE } from '../menu/menu-item.component';
import { VxMenuSeparatorComponent } from '../menu/menu-separator.component';
import { VxMenuTriggerDirective } from '../menu/menu-trigger.directive';
import { orgSwitcherVariants } from './org-switcher.variants';

export interface VxOrgOption {
  id: string;
  name: string;
  /** Tile initial; defaults to the first character of `name`. */
  initial?: string;
  /** Pill label (e.g. a plan name); omitted → no badge. */
  badge?: string;
  /** Badge tone; defaults to accent for the active org, neutral otherwise. */
  badgeTone?: VxBadgeTone;
}

/**
 * Sidebar user card that opens the org-switcher panel above it: user header
 * (name + email, selectable — e.g. to open account settings), selectable org
 * list, and a projected footer for ordinary `<vx-menu-item>`s (log out etc. —
 * labels stay with the consumer):
 *
 *   <vx-org-switcher
 *     userName="…" userEmail="…" [avatarSrc]="…"
 *     [orgs]="orgs" [activeOrgId]="activeId"
 *     (accountSelected)="openAccount()"
 *     (orgSelected)="switchOrg($event)">
 *     <vx-menu-item icon="sign-out" (triggered)="logout()">Log out</vx-menu-item>
 *   </vx-org-switcher>
 *
 * `compact` reduces the trigger to an avatar-only puck (for a collapsed
 * rail); the panel itself is unchanged.
 */
@Component({
  selector: 'vx-org-switcher',
  templateUrl: './org-switcher.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    CdkMenuItem,
    VxAvatarComponent,
    VxBadgeComponent,
    VxIconComponent,
    VxMenuComponent,
    VxMenuSeparatorComponent,
    VxMenuTriggerDirective,
  ],
  host: { class: 'block font-brand' },
  // Projected <vx-menu-item>s are instantiated at their declaration site —
  // outside any open menu — and CdkMenuItem requires MENU_STACK there.
  // Providing the stack here also makes the internal trigger reuse it
  // (parent-or-new), so item activation still closes the panel.
  // The 'tile' appearance makes projected items match the org rows (icon
  // box aligned with the 24px avatar tiles, bright icon).
  providers: [
    { provide: MENU_STACK, useClass: MenuStack },
    { provide: VX_MENU_ITEM_APPEARANCE, useValue: 'tile' },
  ],
})
export class VxOrgSwitcherComponent {
  readonly userName = input.required<string>();

  readonly userEmail = input<string>();

  readonly avatarSrc = input<string>();

  readonly orgs = input.required<VxOrgOption[]>();

  readonly activeOrgId = input<string>();

  /** Fires when the user header in the panel is picked; the panel closes itself. */
  readonly accountSelected = output<void>();

  /** Fires with the chosen org; the panel closes itself. */
  readonly orgSelected = output<VxOrgOption>();

  protected readonly activeOrg = computed(() =>
    this.orgs().find(org => org.id === this.activeOrgId()),
  );

  /** Avatar-only puck for a collapsed rail. */
  readonly compact = input(false);

  protected readonly classes = computed(() => {
    const slots = orgSwitcherVariants({ compact: this.compact() });
    return { trigger: slots.trigger(), text: slots.text() };
  });

  protected readonly userInitials = computed(() =>
    this.userName()
      .split(/\s+/)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join(''),
  );

  protected isActive(org: VxOrgOption): boolean {
    return org.id === this.activeOrgId();
  }

  protected badgeTone(org: VxOrgOption): VxBadgeTone {
    return org.badgeTone ?? (this.isActive(org) ? 'accent' : 'neutral');
  }
}
